import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import 'cross-fetch/polyfill';
import { AppShellContextProvider } from '@exo/frontend-common-app-shell';
import Routes from './Routes';
import { SessionTimeoutManager } from '../SessionTimeoutManager';
import { initializeGAEvents } from '../gaTagManager';
import { useGlobalTracker } from '../useGlobalTracker';
import '@bphxd/ds-core-react/lib/scss/bp-core.scss';
import { initializeGA } from '../../../common/app-shell/client/utils/gaTagManager';

const gtm = `(function(w, d, s, l, i) {w[l] = w[l] || [];w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });var f = d.getElementsByTagName(s)[0],j = d.createElement(s),dl = l != 'dataLayer' ? '&l=' + l : '';j.async = true;j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;f.parentNode.insertBefore(j, f);})(window,document,'script','dataLayer',"${process.env.GTMID}");`;

// GA measurement script
const gaScript = process.env.GA_MEASUREMENT_ID 
  ? `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${process.env.GA_MEASUREMENT_ID}', {
  'page_path': window.location.pathname,
  'send_page_view': true
});`
  : '';

const App = ({ config }) => {
  const [msalInstance, setMsalInstance] = React.useState(null);
  const [msalScopes, setMsalScopes] = React.useState([]);

  // Initialize global tracking hook for auto-tracking button clicks, links, navigation, errors
  useGlobalTracker();

  React.useEffect(() => {
    // Initialize GA when app loads
    initializeGA();
    initializeGAEvents();
    window.setLog = b => {
      window.enableLog = b;
    };

    // Get MSAL instance and scopes from global state if available
    if (typeof window !== 'undefined') {
      const msal = window.msalConfig?.instance;
      const scopes = window.msalConfig?.scopes || [];
      if (msal) {
        setMsalInstance(msal);
        setMsalScopes(scopes);
      }
    }
  }, []);

  const gtmScript = process.env.GTMID && [
    {
      type: 'text/javascript',
      innerHTML: `window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      gtag("consent", "default", {
        ad_storage: "denied",
        analytics_storage: "denied",
        wait_for_update: 2000
      });
      gtag("set", "ads_data_redaction", true);
      function getCookieConsent() {
        try {
          return JSON.parse(window?.localStorage?.cookieConsent);
        } catch {
          return {};
        }
      }
      const cookieConsent = getCookieConsent();
      const analyticsConsent = cookieConsent.hasConsent ? 'granted' : 'denied';

      gtag("consent", "update", {
        ad_storage: "denied",
        analytics_storage: analyticsConsent
      });
      `
    },
    {
      type: 'text/javascript',
      innerHTML: gtm
    },
    // Enable GA if GA_MEASUREMENT_ID is available
    process.env.GA_MEASUREMENT_ID && {
      type: 'text/javascript',
      src: `https://www.googletagmanager.com/gtag/js?id=${process.env.GA_MEASUREMENT_ID}`,
      async: true
    },
    process.env.GA_MEASUREMENT_ID && {
      type: 'text/javascript',
      innerHTML: gaScript
    }
  ].filter(Boolean);
  
  return (
    <div>
      <Helmet script={gtmScript}>
        <title>{process.env.APP_NAME}</title>
        <meta name="description" content="BP NEXTGEN" />
        <meta name="charset" charSet="utf-8" />
        <meta name="keywords" content="demo, boilerplate" />
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no"
        />
        <meta name="theme-color" content="black" />
        <link rel="icon" href="/static/favicon.ico" />
        <link rel="apple-touch-icon" href="/static/favicon.ico" />
      </Helmet>

      {/* Google Tag Manager (noscript) */}
      <noscript>
        <iframe
          title="Google Tag Manager"
          src={`https://www.googletagmanager.com/ns.html?id=${process.env.GTMID}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>

      <AppShellContextProvider config={config}>
        <Routes />
        {/* Session timeout manager for token refresh on idle */}
        {msalInstance && (
          <SessionTimeoutManager 
            msalInstance={msalInstance}
            scopes={msalScopes}
          />
        )}
      </AppShellContextProvider>
    </div>
  );
};

App.propTypes = {
  config: PropTypes.objectOf(
    PropTypes.shape({
      redirects: PropTypes.objectOf(PropTypes.string),
      modules: PropTypes.shape({
        url: PropTypes.string,
        modules: PropTypes.objectOf(PropTypes.func)
      }),
      translations: PropTypes.arrayOf(PropTypes.shape())
    })
  ).isRequired
};

export default App;
